/**
 * Application Configuration Service
 * Handles environment-based configuration for different deployments
 */

class AppConfig {
  constructor() {
    this.runtimeConfig = {};
    this.parentStore = null;

    // Currency Configuration
    this.currency = {
      code: 'TZS',
      symbol: 'TZS',
      decimalPlaces: 2,
    };

    // Localization Configuration
    this.locale = {
      code: 'en-US',
      timezone: null,
      intlTimeZone: null,
      timezoneOffsetMinutes: null,
      timezoneSource: null,
      dateFormat: 'DD/MM/YYYY',
      numberFormat: 'en-US',
    };

    // Regional Configuration
    this.region = {
      countryCode: 'TZ',
      countryName: 'Tanzania',
    };

    this.site = {
      name: 'tanzania',
    };

    // Application Configuration
    this.app = {
      name: 'Electricity Billing System',
      version: '1.0.0',
      company: 'ShopWorx Integration',
    };

    // API Configuration
    this.api = {
      baseUrl: '',
      timeout: 30000,
      enableRealApi: true,
      enableMockData: false,
    };

    // Feature Flags
    this.features = {
      enableMockData: false,
      enableAnalytics: false,
      debugMode: false,
    };

    this.billing = {
      mode: 'auto',
      timeOfUse: false,
      splitDemandByTouPeriod: false,
      useTouUnitCost: true,
    };
  }

  setRuntimeConfig(runtimeConfig = {}) {
    this.runtimeConfig = runtimeConfig;

    if (runtimeConfig.currency) {
      this.currency = { ...this.currency, ...runtimeConfig.currency };
    }

    if (runtimeConfig.locale) {
      this.locale = { ...this.locale, ...runtimeConfig.locale };
      if (runtimeConfig.locale.timezone) {
        this.setTimezone(runtimeConfig.locale.timezone, 'runtime');
      }
    }

    if (runtimeConfig.region) {
      this.region = { ...this.region, ...runtimeConfig.region };
    }

    if (runtimeConfig.site) {
      this.site = { ...this.site, ...runtimeConfig.site };
      if (runtimeConfig.site.name) {
        this.app = { ...this.app, name: runtimeConfig.site.name };
      }
    }

    if (runtimeConfig.app) {
      this.app = { ...this.app, ...runtimeConfig.app };
    }

    if (runtimeConfig.api) {
      this.api = { ...this.api, ...runtimeConfig.api };
    }

    if (runtimeConfig.features) {
      this.features = { ...this.features, ...runtimeConfig.features };
    }

    if (runtimeConfig.billing) {
      this.billing = { ...this.billing, ...runtimeConfig.billing };
    }
  }

  setParentStore(store) {
    this.parentStore = store || null;
  }

  getParentUserDetails() {
    const state = this.parentStore && this.parentStore.state;
    return state && state.user && state.user.me ? state.user.me : null;
  }

  getParentUserName() {
    const userDetails = this.getParentUserDetails();
    const user = userDetails && userDetails.user ? userDetails.user : {};
    const fullName = [
      user.firstname,
      user.lastname,
    ].filter(Boolean).join(' ').trim();

    return fullName || user.emailId || '';
  }

  getParentSiteName() {
    const store = this.parentStore;
    if (!store) {
      return '';
    }

    const siteNameFromGetter = store.getters && store.getters['user/siteName'];
    if (siteNameFromGetter) {
      return siteNameFromGetter;
    }

    const state = store.state || {};
    const userState = state.user || {};
    const { activeSite } = userState;
    const userDetails = this.getParentUserDetails();
    const userSites = userDetails && Array.isArray(userDetails.site)
      ? userDetails.site
      : [];
    const matchingSite = userSites.find((site) => site.id === activeSite);

    if (!matchingSite) {
      return '';
    }

    return matchingSite.name || matchingSite.siteName || matchingSite.site_name || '';
  }

  /**
   * Get currency configuration
   * @returns {Object} Currency configuration
   */
  getCurrency() {
    return this.currency;
  }

  /**
   * Get currency code
   * @returns {string} Currency code (e.g., 'TZS', 'USD', 'EUR')
   */
  getCurrencyCode() {
    return this.currency.code;
  }

  /**
   * Get currency symbol
   * @returns {string} Currency symbol for display
   */
  getCurrencySymbol() {
    return this.currency.symbol;
  }

  /**
   * Get currency decimal places
   * @returns {number} Number of decimal places for currency
   */
  getCurrencyDecimalPlaces() {
    return this.currency.decimalPlaces;
  }

  /**
   * Get locale configuration
   * @returns {Object} Locale configuration
   */
  getLocale() {
    return this.locale;
  }

  /**
   * Get locale code for number/date formatting
   * @returns {string} Locale code (e.g., 'en-US', 'sw-TZ')
   */
  getLocaleCode() {
    return this.locale.code;
  }

  /**
   * Get timezone
   * @returns {string} Timezone identifier
   */
  getTimezone() {
    return this.locale.timezone;
  }

  getIntlTimeZone() {
    return this.locale.intlTimeZone;
  }

  getTimezoneOffsetMinutes() {
    return typeof this.locale.timezoneOffsetMinutes === 'number'
      ? this.locale.timezoneOffsetMinutes
      : null;
  }

  normalizeTimezone(timezone) {
    if (!timezone || typeof timezone !== 'string') {
      return null;
    }

    const trimmedTimezone = timezone.trim();
    const gmtOffsetMatch = trimmedTimezone.match(/^GMT([+-])(\d{2}):?(\d{2})$/i);

    if (gmtOffsetMatch) {
      const [, sign, hours, minutes] = gmtOffsetMatch;
      const offsetMinutes = (parseInt(hours, 10) * 60 + parseInt(minutes, 10))
        * (sign === '-' ? -1 : 1);

      return {
        raw: trimmedTimezone,
        intlTimeZone: offsetMinutes === 0 ? 'UTC' : null,
        offsetMinutes,
      };
    }

    try {
      new Intl.DateTimeFormat(this.locale.code || 'en-US', { timeZone: trimmedTimezone }).format(new Date());
      return {
        raw: trimmedTimezone,
        intlTimeZone: trimmedTimezone,
        offsetMinutes: null,
      };
    } catch (error) {
      return null;
    }
  }

  setTimezone(timezone, source = 'businesshours') {
    const normalizedTimezone = this.normalizeTimezone(timezone);

    if (!normalizedTimezone) {
      console.warn('[BillingApp] Ignoring invalid timezone:', timezone);
      return false;
    }

    this.locale = {
      ...this.locale,
      timezone: normalizedTimezone.raw,
      intlTimeZone: normalizedTimezone.intlTimeZone,
      timezoneOffsetMinutes: normalizedTimezone.offsetMinutes,
      timezoneSource: source,
    };

    return true;
  }

  /**
   * Get regional configuration
   * @returns {Object} Regional configuration
   */
  getRegion() {
    return this.region;
  }

  /**
   * Get country name
   * @returns {string} Country name
   */
  getCountryName() {
    return this.region.countryName;
  }

  getCountryCode() {
    return this.region.countryCode;
  }

  getSite() {
    return this.site;
  }

  getSiteName() {
    return this.getParentSiteName() || this.site.name;
  }

  /**
   * Get application configuration
   * @returns {Object} Application configuration
   */
  getApp() {
    return this.app;
  }

  /**
   * Get application name
   * @returns {string} Application name
   */
  getAppName() {
    return this.site.name || this.app.name;
  }

  /**
   * Get company name
   * @returns {string} Company name
   */
  getCompanyName() {
    return this.app.company;
  }

  getBilling() {
    return this.billing;
  }

  getBillingMode() {
    return this.billing.mode || 'auto';
  }

  getBillingCapabilities() {
    return {
      timeOfUse: this.billing.timeOfUse === true,
      splitDemandByTouPeriod: this.billing.splitDemandByTouPeriod === true,
      useTouUnitCost: this.billing.useTouUnitCost !== false,
    };
  }

  setBillingAppConfig(config = {}) {
    this.currency = {
      ...this.currency,
      code: config.currencyCode || this.currency.code,
      symbol: config.currencySymbol || this.currency.symbol,
      decimalPlaces: parseInt(config.currencyDecimalPlaces, 10) || this.currency.decimalPlaces,
    };

    this.billing = {
      ...this.billing,
      timeOfUse: config.timeOfUse === true,
      splitDemandByTouPeriod: config.splitDemandByTouPeriod === true,
      useTouUnitCost: config.useTouUnitCost !== false,
    };
  }

  getApi() {
    return this.api;
  }

  getApiBaseUrl() {
    return this.api.baseUrl;
  }

  isRealApiEnabled() {
    return this.api.enableRealApi === true;
  }

  isMockDataEnabled() {
    return this.features.enableMockData === true || this.api.enableMockData === true;
  }

  /**
   * Check if a feature is enabled
   * @param {string} featureName - Name of the feature
   * @returns {boolean} True if feature is enabled
   */
  isFeatureEnabled(featureName) {
    return this.features[featureName] || false;
  }

  /**
   * Get all configuration as a single object
   * @returns {Object} Complete configuration
   */
  getAll() {
    return {
      currency: this.currency,
      locale: this.locale,
      region: this.region,
      site: this.site,
      app: this.app,
      api: this.api,
      features: this.features,
      billing: this.billing,
    };
  }

  /**
   * Format currency amount according to configuration
   * @param {number} amount - Amount to format
   * @param {Object} options - Formatting options
   * @returns {string} Formatted currency string
   */
  formatCurrency(amount, options = {}) {
    if (!amount && amount !== 0) return `0 ${this.currency.symbol}`;

    const {
      showSymbol = true,
      useShortFormat = false,
      decimalPlaces = this.currency.decimalPlaces,
    } = options;

    let formattedAmount = amount;

    // Apply short format for large numbers
    if (useShortFormat) {
      if (amount >= 1000000000) {
        formattedAmount = `${(amount / 1000000000).toFixed(1)}B`;
      } else if (amount >= 1000000) {
        formattedAmount = `${(amount / 1000000).toFixed(1)}M`;
      } else if (amount >= 1000) {
        formattedAmount = `${(amount / 1000).toFixed(1)}K`;
      } else {
        formattedAmount = amount.toLocaleString(this.locale.code, {
          minimumFractionDigits: decimalPlaces,
          maximumFractionDigits: decimalPlaces,
        });
      }
    } else {
      formattedAmount = amount.toLocaleString(this.locale.code, {
        minimumFractionDigits: decimalPlaces,
        maximumFractionDigits: decimalPlaces,
      });
    }

    return showSymbol ? `${formattedAmount} ${this.currency.symbol}` : formattedAmount;
  }

  /**
   * Format number according to locale configuration
   * @param {number} value - Number to format
   * @param {number} decimals - Number of decimal places
   * @returns {string} Formatted number string
   */
  formatNumber(value, decimals = 0) {
    if (!value && value !== 0) return '0';

    return value.toLocaleString(this.locale.code, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
  }

  /**
   * Format date according to locale configuration
   * @param {Date|string} date - Date to format
   * @param {Object} options - Formatting options
   * @returns {string} Formatted date string
   */
  formatDate(date, options = {}) {
    if (!date) return '';

    const dateObj = new Date(date);
    if (Number.isNaN(dateObj.getTime())) return 'Invalid Date';

    const defaultOptions = {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    };
    const intlTimeZone = this.getIntlTimeZone();
    if (intlTimeZone) {
      defaultOptions.timeZone = intlTimeZone;
    }

    return dateObj.toLocaleDateString(this.locale.code, { ...defaultOptions, ...options });
  }

  /**
   * Format date and time according to locale configuration
   * @param {Date|string} date - Date to format
   * @param {Object} options - Formatting options
   * @returns {string} Formatted date and time string
   */
  formatDateTime(date, options = {}) {
    if (!date) return '';

    const dateObj = new Date(date);
    if (Number.isNaN(dateObj.getTime())) return 'Invalid Date';

    const defaultOptions = {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    };
    const intlTimeZone = this.getIntlTimeZone();
    if (intlTimeZone) {
      defaultOptions.timeZone = intlTimeZone;
    }

    return dateObj.toLocaleString(this.locale.code, { ...defaultOptions, ...options });
  }
}

// Create and export singleton instance
const appConfig = new AppConfig();

export default appConfig;
export { AppConfig };
